import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { wranglerError } from './errors';

/** Load something from Ranger's Wrangler endpoints: { data, loading, error, reload, setData }. */
const useLoad = (url, initial) => {
  const [state, setState] = useState({ data: initial, loading: true, error: null });

  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get(url);
      setState({ data, loading: false, error: null });
    } catch (err) {
      setState({ data: initial, loading: false, error: wranglerError(err) });
    }
    // `initial` is a constant per hook below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => { reload(); }, [reload]);

  const setData = useCallback((update) => setState((s) => ({ ...s, data: update(s.data) })), []);
  return { ...state, reload, setData };
};

const NO_QUOTAS = [];
const NO_QUESTIONS = { questions_synced_at: null, questions: [] };
const NO_RULES = [];

/**
 * The survey's Wrangler quotas, as Wrangler gives them. replaceQuota() updates one in place;
 * setData() changes the list (adding created quotas, removing deleted ones).
 */
export const useWranglerQuotas = (surveyId) => {
  const { data, setData, ...rest } = useLoad(`/surveys/${surveyId}/wrangler/quotas/`, NO_QUOTAS);
  const replaceQuota = useCallback(
    (updated) => setData((quotas) => quotas.map((q) => (q.id === updated.id ? updated : q))),
    [setData],
  );
  return { quotas: data, replaceQuota, setData, ...rest };
};

export const useWranglerQuestions = (surveyId) => {
  const { data, ...rest } = useLoad(`/surveys/${surveyId}/wrangler/questions/`, NO_QUESTIONS);
  return { questions: data.questions, syncedAt: data.questions_synced_at, ...rest };
};

export const useWranglerRules = (surveyId) => {
  const { data, ...rest } = useLoad(`/surveys/${surveyId}/wrangler/rules/`, NO_RULES);
  return { rules: data, ...rest };
};

/**
 * Every finished import, newest first, from all pages: each quota's review details come from the
 * import that created it, and the not-converted banner from the newest.
 */
export const useImports = (surveyId) => {
  const [state, setState] = useState({ jobs: [], loading: true, error: null });

  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const jobs = [];
      let page = 0;
      let more = true;
      while (more) {
        page += 1;
        const { data } = await api.get(
          `/surveys/${surveyId}/wrangler/jobs/?kind=import_alchemer_quotas&status=done&page=${page}`,
        );
        jobs.push(...data.results);
        more = Boolean(data.has_next);
      }
      setState({ jobs, loading: false, error: null });
    } catch (err) {
      setState({ jobs: [], loading: false, error: wranglerError(err) });
    }
  }, [surveyId]);

  useEffect(() => { reload(); }, [reload]);
  return { ...state, reload };
};

/** Alchemer's own count for each Alchemer quota ID, to show beside its Wrangler copy. */
export const useAlchemerCounts = (surveyId) => {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    api.get(`/surveys/${surveyId}/quotas/`)
      .then(({ data }) => setCounts(Object.fromEntries(
        data.quotas.filter((q) => q.system === 'alchemer').map((q) => [q.id, q.responses]),
      )))
      .catch(() => setCounts({}));
  }, [surveyId]);
  return counts;
};
